import { useEffect, useRef, useState } from "react";
import { IconMaximize, IconMinimize } from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import { ViewerToolbarButton } from "./ViewerToolbar";

/**
 * A region of the page that the reader can put full screen (Figma
 * "D3.infographic.fullscreen"). The region hands its toggle to its content,
 * which places it in its floating bar. Full screen, the region turns dark;
 * Escape, or the toggle, leaves it. The browser owns the full screen state:
 * this only mirrors it, so a region left with Escape shows the right toggle.
 *
 * A UI primitive: the element ref and the mirrored state are machinery.
 */
export function FullscreenRegion({
  enterLabel,
  exitLabel,
  className,
  children,
}: {
  enterLabel: string;
  exitLabel: string;
  /** The region's classes outside full screen. */
  className: string;
  children: (toggle: React.ReactNode) => React.ReactNode;
}): React.JSX.Element {
  const regionRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(
        document.fullscreenElement !== null &&
          document.fullscreenElement === regionRef.current
      );
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
    };
  }, []);

  const toggle = (
    <ViewerToolbarButton
      label={isFullscreen ? exitLabel : enterLabel}
      isDisabled={false}
      onClick={() => {
        if (isFullscreen) {
          void document.exitFullscreen();
          return;
        }
        const region = regionRef.current;
        if (region === null) {
          throw new Error("should not happen: no region after mount");
        }
        void region.requestFullscreen();
      }}
    >
      {isFullscreen ? (
        <IconMinimize stroke={1.5} />
      ) : (
        <IconMaximize stroke={1.5} />
      )}
    </ViewerToolbarButton>
  );

  return (
    <div
      ref={regionRef}
      className={cn("relative", isFullscreen ? "bg-ds-black" : className)}
    >
      {children(toggle)}
    </div>
  );
}
